import { ReactNode } from "react";

import Dropdown from "components/Dropdown";
import { DocumentIcon, MedalIcon } from "components/icons";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { TTabItem } from "./types";
import { ENavTab } from "components/Layout/types";

import styles from "./Tab.module.css";

export interface TabProps {
  id?: string;
  items: TTabItem;
  selected?: string;
  buttonClassName?: string;
  onChange?: (key: string, path?: string) => void;
}

const TAB_CHILD_ICON_MAP: Partial<Record<ENavTab, ReactNode>> = {
  [ENavTab.SCORECARD_HISTORY]: <MedalIcon height={16} />,
  [ENavTab.PAST_NOTES]: <DocumentIcon height={16} />,
};

const Tab = ({ id, items, selected, buttonClassName, onChange }: TabProps) => {
  const { t } = useTranslationContext();

  const selectedKey = selected ?? Object.keys(items)?.[0];

  return (
    <div data-testid={id} id={id} className="w-full h-full">
      <div
        className={cn(
          "flex justify-left flex-nowrap gap-6 items-center h-full",
          styles.container
        )}
      >
        {Object.keys(items).map((key) => {
          const tab = items[key];
          const isActive = key === selectedKey;

          if (tab.children) {
            const dropdownItems = Object.values(tab.children).map((child) => ({
              label: t(child.label),
              icon: child.iconKey && TAB_CHILD_ICON_MAP[child.iconKey],
              onClick: () => onChange?.(key, child.path),
            }));

            return (
              <div key={key} className="flex flex-col h-full">
                <Dropdown
                  trigger={t(tab.label)}
                  active={isActive}
                  items={dropdownItems}
                />
                {isActive && <div className={styles.activeTab} />}
              </div>
            );
          }

          return (
            <button
              key={key}
              data-testid={`tab-button-${id ? `${id}-` : ""}${key}`}
              className={cn(
                styles.label,
                "text-gray-900 h-full flex flex-col text-base",
                isActive && "text-primary-500 font-semibold",
                buttonClassName
              )}
              disabled={isActive}
              onClick={() => onChange?.(key)}
            >
              <div className="flex items-center h-full">
                <div className={cn(isActive && "mt-1")}>{t(tab.label)}</div>
              </div>
              {isActive && <div className={styles.activeTab} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Tab;
